import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import {
  PatientStamkortFields,
} from '@/components/patient/PatientStamkortFields';
import { PatientPortalSection } from '@/components/patient/PatientPortalSection';
import type { TelephoneSubscriberDraft } from '@/lib/patientStamkortFields';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { PatientRow } from '@/hooks/usePatients';

export const CPR_CLEAR = '__CLEAR__';

interface AddressShape {
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

export function EditPatientSlideOver({
  open,
  patient,
  firstName,
  lastName,
  email,
  jobTitle,
  telephoneSubscribers,
  address,
  notes,
  cave,
  marketingEmail,
  marketingSms,
  appointmentEmailOptOut,
  preferredLocale,
  cprChange,
  canEditCpr,
  canArchive,
  canManagePortal,
  fieldErrors,
  fieldsSubmitted,
  submitError,
  saving,
  archiving,
  dirty,
  onClose,
  onSubmit,
  onArchive,
  setFirstName,
  setLastName,
  setEmail,
  setJobTitle,
  setTelephoneSubscribers,
  setAddress,
  setNotes,
  setCave,
  setMarketingEmail,
  setMarketingSms,
  setAppointmentEmailOptOut,
  setPreferredLocale,
  setCprChange,
}: {
  open: boolean;
  patient: PatientRow & { has_cpr: boolean };
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  telephoneSubscribers: TelephoneSubscriberDraft[];
  address: AddressShape;
  notes: string;
  cave: string;
  marketingEmail: boolean;
  marketingSms: boolean;
  appointmentEmailOptOut: boolean;
  preferredLocale: 'da-DK' | 'en-DK';
  cprChange: string | null;
  canEditCpr: boolean;
  canArchive: boolean;
  canManagePortal: boolean;
  fieldErrors: { email?: string };
  fieldsSubmitted: boolean;
  submitError: string | null;
  saving: boolean;
  archiving: boolean;
  dirty: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
  onArchive: () => void;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setEmail: (value: string) => void;
  setJobTitle: (value: string) => void;
  setTelephoneSubscribers: (value: TelephoneSubscriberDraft[]) => void;
  setAddress: (value: AddressShape) => void;
  setNotes: (value: string) => void;
  setCave: (value: string) => void;
  setMarketingEmail: (value: boolean) => void;
  setMarketingSms: (value: boolean) => void;
  setAppointmentEmailOptOut: (value: boolean) => void;
  setPreferredLocale: (value: 'da-DK' | 'en-DK') => void;
  setCprChange: (value: string | null) => void;
}) {
  const { t } = useTranslation();

  return (
    <SlideOver
      open={open}
      title={t('patients.header.edit')}
      description={t('patients.edit_slideover.description')}
      onClose={onClose}
      dirty={dirty}
      footer={
        <div className="flex w-full items-center gap-s2">
          {canArchive ? (
            <Button
              type="button"
              variant="ghost"
              className="text-ds-red-ink hover:bg-ds-red-tint hover:text-ds-red-ink"
              onClick={onArchive}
              disabled={archiving || saving}
            >
              {patient.status === 'active' ? t('patients.archive') : t('patients.restore')}
            </Button>
          ) : null}
          <div className="ml-auto">
            <SlideOverFooter
              onCancel={onClose}
              primary={
                <Button
                  type="submit"
                  form="edit-patient-form"
                  loading={saving}
                  loadingText={t('common.saving')}
                  disabled={!firstName.trim() || !lastName.trim()}
                >
                  {t('patients.edit_slideover.save')}
                </Button>
              }
            />
          </div>
        </div>
      }
    >
      <form id="edit-patient-form" onSubmit={onSubmit} className="flex flex-col gap-s5">
        {submitError ? <ErrorAlert message={submitError} /> : null}

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.section_basic')}</h3>
          <div className="grid gap-s4 nav:grid-cols-2">
            <Input
              label={t('patients.field_first_name')}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
            <Input
              label={t('patients.field_last_name')}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              required
            />
            <Input
              label={t('patients.field_email')}
              name="email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={fieldErrors.email}
            />
          </div>
        </section>

        <PatientStamkortFields
          presentation="sections"
          jobTitle={jobTitle}
          onJobTitleChange={setJobTitle}
          subscribers={telephoneSubscribers}
          onSubscribersChange={setTelephoneSubscribers}
          submitted={fieldsSubmitted}
        />

        {canEditCpr ? (
          <section>
            <h3 className={cn(TYPE.section, 'mb-s2 text-ds-text')}>{t('patients.section_cpr')}</h3>
            <p className={cn(TYPE.small, 'mb-s3 text-ds-muted')}>{t('patients.cpr_encryption_note')}</p>
            {cprChange === null ? (
              <div className="flex flex-wrap items-center gap-s2">
                <span className={cn(TYPE.body, 'text-ds-text')}>
                  {patient.has_cpr ? t('patients.cpr_currently_set') : t('patients.cpr_currently_unset')}
                </span>
                <Button type="button" variant="secondary" onClick={() => setCprChange('')}>
                  {patient.has_cpr ? t('patients.cpr_replace') : t('patients.cpr_set')}
                </Button>
                {patient.has_cpr ? (
                  <Button type="button" variant="ghost" onClick={() => setCprChange(CPR_CLEAR)}>
                    {t('patients.cpr_clear')}
                  </Button>
                ) : null}
              </div>
            ) : cprChange === CPR_CLEAR ? (
              <div className="flex flex-wrap items-center gap-s2">
                <span className={cn(TYPE.body, 'text-ds-amber-ink')}>
                  {t('patients.cpr_will_clear_on_save')}
                </span>
                <Button type="button" variant="ghost" onClick={() => setCprChange(null)}>
                  {t('common.undo')}
                </Button>
              </div>
            ) : (
              <Input
                label={t('patients.field_cpr')}
                name="cpr"
                value={cprChange}
                onChange={(event) => setCprChange(event.target.value)}
                hint={t('patients.field_cpr_hint')}
                autoFocus
              />
            )}
          </section>
        ) : null}

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.section_address')}</h3>
          <div className="grid gap-s4 nav:grid-cols-2">
            <div className="nav:col-span-2">
              <Input
                label={t('patients.field_street')}
                value={address.street}
                onChange={(event) => setAddress({ ...address, street: event.target.value })}
              />
            </div>
            <Input
              label={t('patients.field_zip')}
              value={address.zip_code}
              onChange={(event) => setAddress({ ...address, zip_code: event.target.value })}
            />
            <Input
              label={t('patients.field_city')}
              value={address.city}
              onChange={(event) => setAddress({ ...address, city: event.target.value })}
            />
            <div className="nav:col-span-2">
              <Input
                label={t('patients.field_country')}
                value={address.country}
                onChange={(event) => setAddress({ ...address, country: event.target.value })}
              />
            </div>
          </div>
        </section>

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.section_notes')}</h3>
          <Textarea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </section>

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.field_cave')}</h3>
          <Textarea
            rows={2}
            value={cave}
            onChange={(event) => setCave(event.target.value)}
            hint={t('patients.field_cave_hint')}
          />
        </section>

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.section_consent')}</h3>
          <div className="flex flex-col gap-s3">
            <Checkbox
              checked={marketingEmail}
              onChange={(event) => setMarketingEmail(event.target.checked)}
              label={t('patients.consent_marketing_email')}
              hint={t('patients.consent_marketing_email_desc')}
            />
            <Checkbox
              checked={marketingSms}
              onChange={(event) => setMarketingSms(event.target.checked)}
              label={t('patients.consent_marketing_sms')}
              hint={t('patients.consent_marketing_sms_desc')}
            />
            <Checkbox
              checked={appointmentEmailOptOut}
              onChange={(event) => setAppointmentEmailOptOut(event.target.checked)}
              label={t('patients.consent_appointment_email_opt_out')}
              hint={t('patients.consent_appointment_email_opt_out_desc')}
            />
          </div>
        </section>

        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>
            {t('patients.section_language', 'Sprog')}
          </h3>
          <Select
            id="patient-locale"
            label={t('patients.field_preferred_locale', 'Foretrukket sprog')}
            value={preferredLocale}
            onChange={(event) => setPreferredLocale(event.target.value as 'da-DK' | 'en-DK')}
            hint={t(
              'patients.field_preferred_locale_hint',
              'Bruges til spørgeskema-invitationer fra Clinio Forms',
            )}
          >
            <option value="da-DK">{t('patients.locale_da')}</option>
            <option value="en-DK">{t('patients.locale_en')}</option>
          </Select>
        </section>

        {canManagePortal ? (
          <section>
            <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{t('patients.portal.title')}</h3>
            <PatientPortalSection patient={patient} canManage={canManagePortal} variant="inline" />
          </section>
        ) : null}
      </form>
    </SlideOver>
  );
}
